import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useApiUserApplicationsPartialUpdate } from "@/api/lctApIComponents";
import { useNavigate } from "@tanstack/react-router";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";
import { ArrowLeft, InfoIcon } from "lucide-react";
import { ApplicationStatus, defaultStatus, statusDetails } from "@/utils/constants";
import { AlertDialog } from "@/components/AlertDialog";
import { useAuth } from "@/auth/hooks/useAuth";
import { useFormInstances } from "@/hooks/useFormInstances";
import { useState } from "react";

interface IProps {
  pvpId: string;
  appId: string;
  userAppId: string;
  formInstances: ReturnType<typeof useFormInstances>;
}

export const PVPAppFormFooter: React.FC<IProps> = ({ pvpId, appId, userAppId, formInstances }) => {
  const { isStaff } = useAuth();
  const backLink = isStaff ? "/admin/pvp" : "/pvp";
  const token = useAppSelector((state) => state.user.authKey);
  const navigate = useNavigate();
  const { mutateAsync: submitApplication, isPending: isSubmitting } = useApiUserApplicationsPartialUpdate();
  const { appStatus, refetchUserAppData, currentPart, isSubmittable, applicationEditable } = useUserAppInfo(userAppId);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const hasDirtyForms = formInstances.hasDirtyForms();
  const handleExit = () => {
    if (hasDirtyForms) {
      setShowExitDialog(true);
    } else {
      navigate({ to: backLink });
    }
  };

  const statusDetail = appStatus ? (statusDetails.get(appStatus) ?? defaultStatus) : defaultStatus;

  const handleSubmitApplication = async () => {
    if (!appId || !userAppId || !pvpId) {
      console.error("Missing required parameters for submission.");
      return;
    }
    try {
      const response = await submitApplication({
        pathParams: {
          id: userAppId,
        },
        body: {
          status: statusDetail.nextStatus,
        },
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      if (response) {
        refetchUserAppData();
      }
    } catch (error) {
      console.error("Failed to submit the application:", error);
    }
  };

  const handleSaveAllForms = async () => {
    setIsSavingAll(true);
    try {
      await formInstances.saveAllForms();
    } finally {
      setIsSavingAll(false);
    }
  };

  const getAlertContent = () => {
    if (appStatus === ApplicationStatus.APPROVED) {
      return "Application has been approved";
    }
    if (appStatus === ApplicationStatus.PART_2_SUBMITTED) {
      return "Application awaiting review";
    }
    if (hasDirtyForms) {
      return "You have unsaved changes. Please save all changes before submitting.";
    }
    if (appStatus === ApplicationStatus.PART_1_APPROVED && !applicationEditable) {
      return "Part 2 sections need to be completed";
    }
    if (!applicationEditable) {
      return "This part has been submitted";
    }
    return "";
  };

  const alertContent = getAlertContent();
  const showAlert = !applicationEditable || hasDirtyForms;
  const canResubmitPart2 = appStatus === ApplicationStatus.FURTHER_INFO_PART_2;
  const submissionDisabled = !applicationEditable || isSubmitting || (!isSubmittable && !canResubmitPart2) || isSavingAll;

  return (
    <>
      <footer
        className={`flex flex-col border-t border-slate-300 bg-white p-4 py-3 md:flex-row md:items-center md:justify-between md:px-6 md:py-4`}
      >
        <div className="flex w-full flex-row justify-between gap-2 md:order-1 md:w-auto">
          <Button className="border border-slate-300 bg-slate-100" variant="secondary" onClick={handleExit}>
            <div className="flex flex-row items-center justify-between text-sm">
              <ArrowLeft className="slate-900 mr-2 h-4 w-4" />
              Exit<span className="ml-1 hidden sm:flex"> Application</span>
            </div>
          </Button>

          <div className="flex gap-2 md:hidden">
            <Button
              variant="outline"
              onClick={handleSaveAllForms}
              disabled={isSavingAll || !applicationEditable}
              loading={isSavingAll}
            >
              Save All
            </Button>
            <Button onClick={() => setShowConfirmDialog(true)} disabled={submissionDisabled} loading={isSubmitting}>
              {statusDetail.buttonText}
            </Button>
          </div>
        </div>

        {showAlert && alertContent && (
          <div className="my-2 flex w-full flex-row items-start px-1 text-xs leading-tight text-slate-500 sm:order-2 sm:items-center sm:justify-center sm:text-sm sm:leading-normal md:mx-4 md:my-0 md:flex-1">
            <InfoIcon className="mr-1 mt-0.5 h-3 w-3 flex-shrink-0 sm:h-4 sm:w-4" />
            <span className="max-w-full break-words">{alertContent}</span>
          </div>
        )}

        <div className="hidden gap-2 md:order-3 md:flex">
          <Button
            variant="outline"
            onClick={handleSaveAllForms}
            disabled={isSavingAll || !applicationEditable}
            loading={isSavingAll}
          >
            Save All
          </Button>
          <Button onClick={() => setShowConfirmDialog(true)} disabled={submissionDisabled} loading={isSubmitting}>
            {statusDetail.buttonText}
          </Button>
        </div>
      </footer>
      <AlertDialog
        showDialog={showConfirmDialog}
        setShowDialog={setShowConfirmDialog}
        onConfirm={handleSubmitApplication}
        title={`Submit Part ${currentPart}`}
        description={`Are you sure you want to submit part ${currentPart} of your application? ${hasDirtyForms ? "You have unsaved changes. Save changes before submitting to avoid data loss." : ""}`}
        actionText="Submit"
        cancelText="Cancel"
        secondaryActionText={hasDirtyForms ? "Save all data" : undefined}
        onSecondaryAction={hasDirtyForms ? handleSaveAllForms : undefined}
        secondaryActionDisabled={isSavingAll}
        secondaryActionLoading={isSavingAll}
      />
      <AlertDialog
        showDialog={showExitDialog}
        setShowDialog={setShowExitDialog}
        onConfirm={() => navigate({ to: backLink })}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to exit? Any unsaved changes will be lost."
        actionText="Exit Anyway"
      />
    </>
  );
};
